import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Users } from "lucide-react";

interface Participant {
  user_id: string;
  role: string;
  profiles: {
    display_name: string;
    avatar_url?: string;
  };
}

interface SprintCollaborationHeaderProps {
  sprintId: string;
}

export const SprintCollaborationHeader = ({ sprintId }: SprintCollaborationHeaderProps) => {
  const [participants, setParticipants] = useState<Participant[]>([]);

  useEffect(() => {
    loadParticipants();
  }, [sprintId]);

  const loadParticipants = async () => {
    const { data } = await supabase
      .from('sprint_participants')
      .select(`
        user_id,
        role,
        profiles:user_id (
          display_name,
          avatar_url
        )
      `)
      .eq('sprint_id', sprintId);

    if (data) setParticipants(data as any);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  if (participants.length <= 1) return null;

  return (
    <div className="bg-card border rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-muted-foreground" />
          <span className="font-medium">Sprint Team ({participants.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {participants.map((participant) => (
            <div key={participant.user_id} className="flex items-center gap-2">
              <Avatar className="w-8 h-8">
                <AvatarImage src={participant.profiles?.avatar_url} />
                <AvatarFallback className="text-xs">
                  {getInitials(participant.profiles?.display_name || 'User')}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:block">
                <p className="text-sm font-medium">{participant.profiles?.display_name}</p>
                {participant.role === 'creator' && (
                  <Badge variant="secondary" className="text-xs">Creator</Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
